import * as React from 'react';
import { WithRouterProps } from 'react-router';
import { withRouter } from 'app/context/Router';
import CurrentUser from 'auth/CurrentUser';
import ToastActions from 'actions/ToastActions';
import { ConfirmableDeviceType } from 'entities/Device';
// @ts-ignore
import { MERGING_USER_PATH } from 'user/UserConstants';
import ErrorReporter from 'app/components/ErrorReporter';
import { SendType } from 'user/components/LinkDeviceVerificationCodeBox';
import VerifyDeviceCodeContainer from 'user/containers/VerifyDeviceCodeContainer';
import ClaimDeviceContainer from 'user/containers/ClaimDeviceContainer';
import { ClaimDevice_claimDevice } from 'user/mutations/__generated__/ClaimDevice';
import LinkDeviceVerificationCodeBox from 'user/components/LinkDeviceVerificationCodeBox';

export enum VerifyDeviceStatus {
  VerifyDeviceCode = 'verify_device_code',
  ClaimDevice = 'claim_device',
}

export type Props = {
  sendType: SendType;
  submittedAddress: string;
  deviceType: ConfirmableDeviceType;
  footer?: React.ReactNode;
  skipUpdateDevicePreferences?: boolean;
  claimToken?: string;
  status?: VerifyDeviceStatus;
  onResend: () => void;
  onDone: (authToken?: string, deviceUid?: string) => void;
} & WithRouterProps;

type State = {
  claimToken: string;
  mergeAccounts: boolean;
  status: VerifyDeviceStatus;
};

const getStateFromProps = (props: Props): State => ({
  mergeAccounts: true,
  claimToken: props.claimToken ?? '',
  status: props.status ?? VerifyDeviceStatus.VerifyDeviceCode,
});

export class VerifyDeviceContainer extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = getStateFromProps(props);
  }

  userCanClaimAnExistingDevice = () => {
    return (
      CurrentUser.isSignedIn() && CurrentUser.getUser()?.hasFeature('claim_devices.web.july-31')
    );
  };

  onDeviceVerified = (authToken?: string, deviceUid?: string) => {
    const readableDevice = this.props.deviceType === 'email' ? 'Email address' : 'Phone number';
    ToastActions.showSuccessToast(`${readableDevice} linked to Remind`);

    this.props.onDone(authToken, deviceUid);
  };

  onDeviceExists = (claimToken: string) => {
    if (this.userCanClaimAnExistingDevice()) {
      this.setState({ claimToken, status: VerifyDeviceStatus.ClaimDevice });
    } else {
      ToastActions.showSuccessToast(
        'This device is already taken, please contact support to merge the device.'
      );
    }
  };

  onMergingAccounts = async (claimedDevice: ClaimDevice_claimDevice) => {
    this.props.onDone();

    this.props.router.replace({
      pathname: MERGING_USER_PATH,
      state: {
        claimedDevice,
      },
    });
  };

  renderDeviceCodeVerification = () => {
    return (
      <VerifyDeviceCodeContainer
        footer={this.props.footer}
        sendType={this.props.sendType}
        deviceType={this.props.deviceType}
        deviceAddress={this.props.submittedAddress}
        skipUpdateDevicePreferences={this.props.skipUpdateDevicePreferences}
        onResend={this.props.onResend}
        onDeviceVerified={this.onDeviceVerified}
        onDeviceExists={this.onDeviceExists}
      />
    );
  };

  renderClaimDevice = () => {
    const { claimToken } = this.state;

    if (!claimToken) {
      return (
        <ErrorReporter error="VerifyDeviceContainer - requires claim token when claiming a device" />
      );
    }

    return (
      <ClaimDeviceContainer
        claimToken={claimToken}
        deviceType={this.props.deviceType}
        deviceAddress={this.props.submittedAddress}
        onDeviceClaimed={() => this.onDeviceVerified()}
        onMergingAccounts={this.onMergingAccounts}
      />
    );
  };

  renderInvalidStatus = () => {
    return <ErrorReporter error="VerifyDeviceContainer - invalid status" />;
  };

  render() {
    if (!this.userCanClaimAnExistingDevice()) {
      return (
        <LinkDeviceVerificationCodeBox
          skipUpdateDevicePreferences={this.props.skipUpdateDevicePreferences}
          onDone={this.onDeviceVerified}
          submittedAddress={this.props.submittedAddress}
          onResend={this.props.onResend}
          footer={this.props.footer}
          deviceType={this.props.deviceType}
          sendType={this.props.sendType}
        />
      );
    }

    switch (this.state.status) {
      case VerifyDeviceStatus.VerifyDeviceCode:
        return this.renderDeviceCodeVerification();
      case VerifyDeviceStatus.ClaimDevice:
        return this.renderClaimDevice();
      default:
        return this.renderInvalidStatus();
    }
  }
}

export default withRouter(VerifyDeviceContainer);
